import { PlusOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client/react';
import {
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Steps,
} from 'antd';
import clsx from 'clsx';
import {
  filter,
  findIndex,
  flattenDeep,
  includes,
  keys,
  map,
  omit,
  uniq,
  uniqBy,
  values,
} from 'lodash';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useMedia } from 'react-use';
import { AppContext } from '../../AppContext';
import countryCodeData from '../../assets/countryCode.json';
import { EditButton, MinusCircle, WrongIcon } from '../../assets/svg';
import {
  ACCESS_TYPE,
  ADD_USER_ROLES,
  ADD_USER_STEPS,
  BREAKPOINTS,
  GA_EVENT,
  GA_LABEL,
  MOBILE_NUMBER_NOTE,
  REGEX,
  USER_ROLES,
} from '../../common/constants';
import { Event } from '../../common/trackEvents';
import {
  formValidatorRules,
  getPhoneCodeFromTimezone,
  titleCase,
} from '../../common/utils';
import CommonCard from '../../components/CommonCard';
import CommonDropdown from '../../components/CommonDropdown';
import CommonSelect from '../../components/CommonSelect';
import CommonTooltip from '../../components/CommonTooltip';
import EllipsisText from '../../components/EllipsisText';
import HasAccess from '../../components/HasAccess';
import PhoneNumberInputWithCountyCode from '../../components/PhoneNumberInputWithCountyCode';
import AddAgencyModal from '../agencies/AddAgencyModal';
import { CREATE_USER, UPDATE_USER } from './graphql/Mutations';
import { GLOBAL_AGENCY_DROPDOWN_LIST } from './graphql/Queries';

const { required, email } = formValidatorRules;

const AddUserModal = (props) => {
  const {
    isUpdate,
    showModal,
    setShowModal,
    userData,
    setUserData,
    refetchUserDataWithInitialValues,
    isTryingActivate,
    setIsTryingActivate,
  } = props;
  const hasInspectionAccess = !!HasAccess({
    type: ACCESS_TYPE.INSPECTION,
  });
  const isTabletViewport = useMedia(`(min-width: ${BREAKPOINTS.tablet}px)`);
  const isDesktopViewport = useMedia(`(min-width: ${BREAKPOINTS.desktop}px)`);
  const { getCurrentUser, getTenantUser } = useContext(AppContext);
  const [current, setCurrent] = useState(0);
  const [step1formDetails, setStep1formDetails] = useState();
  const [agencyList, setAgencyList] = useState([]);
  const currentUser = getCurrentUser();
  const currentTenant = getTenantUser();
  const [showAgencyModal, setSetShowAgencyModal] = useState(false);
  const [agencyData, setAgencyData] = useState();
  const [alreadyAddedUser, setAlreadyAddedUser] = useState();
  const [globalAgencyList, setGlobalAgencyList] = useState([]);
  const [selectedAgencyData, setSelectedAgencyData] = useState([]);
  const [initialAgencyData, setInitialAgencyData] = useState([]);
  const [showWarningMessage, setShowWarningMessage] = useState(false);
  const [showMainTeamRemoveNote, setShowMainTeamRemoveNote] = useState(false);
  const [showChecklistMakerNote, setShowChecklistMakerNote] = useState(false);
  const [form] = Form.useForm();

  const [createUserMutate, { loading: createUserLoading }] = useMutation(
    CREATE_USER,
    {
      onError() {},
      onCompleted(res) {
        setAlreadyAddedUser(res?.createUser?.user);
        if (!res?.createUser?.user) {
          Event(GA_EVENT.ADD_GLOBAL_USER, {
            label: GA_LABEL.ADD_GLOBAL_USER,
            // eslint-disable-next-line no-undef
            pathname: window?.location?.href,
            user_id: currentUser?.id,
            user_name: currentUser?.name,
            tenant_id: currentUser?.tenantUser?.tenant?.id,
            tenant_name: currentUser?.tenantUser?.tenant?.organizationName,
          });
          setUserData();
          form.resetFields();
          setShowModal(false);
          refetchUserDataWithInitialValues();
        }
      },
    },
  );

  const [updateUserMutate, { loading: updateUserLoading }] = useMutation(
    UPDATE_USER,
    {
      onError() {},
      onCompleted() {
        Event(GA_EVENT.EDIT_GLOBAL_USER, {
          label: GA_LABEL.EDIT_GLOBAL_USER,
          // eslint-disable-next-line no-undef
          pathname: window?.location?.href,
          updated_user_id: userData?.id,
          user_id: currentUser?.id,
          user_name: currentUser?.name,
          tenant_id: currentUser?.tenantUser?.tenant?.id,
          tenant_name: currentUser?.tenantUser?.tenant?.organizationName,
        });
        setUserData();
        if (setIsTryingActivate) {
          setIsTryingActivate(false);
        }
        form.resetFields();
        setShowModal(false);
        refetchUserDataWithInitialValues();
      },
    },
  );

  const handleCancel = () => {
    setShowModal(false);
    form.resetFields();
    setUserData();
    if (setIsTryingActivate) {
      setIsTryingActivate(false);
    }
  };

  useMemo(() => {
    if (currentTenant?.tenant?.timeZone && !userData?.countryCode) {
      const phoneCode = getPhoneCodeFromTimezone(
        currentTenant?.tenant?.timeZone,
      );
      form.setFieldsValue({
        countryCode: phoneCode,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTenant?.tenant?.timeZone]);

  useEffect(() => {
    if (userData?.agencies) {
      setInitialAgencyData(userData?.agencies);
      setSelectedAgencyData(userData?.agencies);
    }
  }, [userData]);

  const onUserSubmitFinish = async (
    formValues,
    isUpdateExistingUser = false,
  ) => {
    if (current === 0) {
      const newFormValues = {
        ...formValues,
        phoneNo: `${formValues?.countryCode}${formValues?.phoneNo}`,
        country: filter(
          countryCodeData,
          (record) => record?.dialCode === formValues?.countryCode,
        )?.[0]?.countryCode?.toUpperCase(),
      };
      setStep1formDetails(newFormValues);
      setCurrent(1);
      form.setFieldsValue({
        agencyIds: uniq(
          map(
            selectedAgencyData?.length > 0
              ? [...selectedAgencyData]
              : [...(userData?.agencies || [])],
            (record) => record?.id,
          ),
        ),
      });
      return;
    }
    const newFormValues = {
      ...step1formDetails,
      ...formValues,
      agencyData: agencyList,
    };

    const variables = isUpdate
      ? {
          data: { ...newFormValues, isActive: isTryingActivate },
          id: userData?.id,
        }
      : {
          ...newFormValues,
          useExistingUser: !!alreadyAddedUser && !isUpdateExistingUser,
          updateExistingUser: isUpdateExistingUser,
        };

    try {
      if (isUpdate) {
        updateUserMutate({
          variables: { ...variables },
        });
        return;
      }
      createUserMutate({
        variables: { data: { ...variables } },
      });
    } catch (error) {
      return error;
    }
  };

  const handleAdd = () => {
    setShowModal(true);
    form.submit();
  };
  const handleSelect = (value) => {
    if (isUpdate) {
      if (
        !includes(userData?.roles, ADD_USER_ROLES.CHECKLIST_MAKER) &&
        value === ADD_USER_ROLES.CHECKLIST_MAKER
      ) {
        setShowChecklistMakerNote(true);
      } else {
        setShowChecklistMakerNote(false);
      }
    }
  };

  const handleEdit = (user) => {
    setSetShowAgencyModal(true);
    setAgencyData(user);
  };

  const step1Form = (
    <>
      <Form.Item
        rules={[
          required,
          {
            max: 250,
            message: 'Username cannot be more than 250 characters',
          },
        ]}
        name="name"
        label="Name"
      >
        <Input id="add-user-name-input" allowClear placeholder="Enter Name" />
      </Form.Item>
      <Form.Item
        rules={[{ required, message: 'Required' }]}
        name="roles"
        label="Roles"
      >
        <CommonSelect
          id="add-user-roles-select"
          className="mr-3"
          placeholder="Select Role"
          allowClear
          showSearch
          optionFilterProp="label"
          onChange={handleSelect}
          disabled={includes(userData?.roles, USER_ROLES.SYSTEM_ADMIN)}
          options={[
            ...(includes(userData?.roles, USER_ROLES.SYSTEM_ADMIN)
              ? [
                  {
                    key: USER_ROLES.SYSTEM_ADMIN,
                    value: USER_ROLES.SYSTEM_ADMIN,
                    label: titleCase(USER_ROLES.SYSTEM_ADMIN),
                  },
                ]
              : []),
            ...values(
              hasInspectionAccess
                ? ADD_USER_ROLES
                : omit(ADD_USER_ROLES, USER_ROLES.CHECKLIST_MAKER),
            ).map((role) => {
              return {
                key: role,
                value: role,
                label: titleCase(role),
              };
            }),
          ]}
        />
      </Form.Item>
      <Form.Item
        rules={[isUpdate ? undefined : required, email]}
        name="email"
        label="Email"
      >
        <Input
          id="add-user-email-input"
          allowClear
          placeholder="Enter Email"
          onKeyDown={(e) => {
            if (e?.code === 'Space') {
              e?.preventDefault();
            }
          }}
        />
      </Form.Item>
      <Form.Item
        label={
          <>
            <span className="required-mark">*</span> Mobile Number
          </>
        }
        name="phoneNo"
        dependencies={['countryCode']}
        rules={[
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!getFieldValue('countryCode')) {
                return Promise.reject(new Error('Please Select Country Code'));
              }
              if (!value) {
                return Promise.reject(new Error('Enter Mobile Number'));
              }
              if (value && !value.match(REGEX.PHONE)) {
                return Promise.reject(
                  new Error('should be a valid contact number'),
                );
              }
              return Promise.resolve();
            },
          }),
        ]}
      >
        <PhoneNumberInputWithCountyCode id="add-user-phone-input" />
      </Form.Item>
      <div className="text-danger note-text note-margin ">
        Note : {MOBILE_NUMBER_NOTE}
      </div>
      {showChecklistMakerNote && (
        <div className="text-danger mb-20">
          <b>Caution:</b> Changing role to Checklist Maker will remove this user
          from all the assigned projects
        </div>
      )}
      <div className="form-buttons">
        <Button
          id="next-button"
          shape="round"
          type="primary"
          disabled={false}
          className="save-button"
          htmlType="submit"
        >
          Next
        </Button>
      </div>
    </>
  );
  const step2Form = (
    <>
      <>
        <Form.Item
          name="agencyIds"
          label={
            <div className="d-flex justify-between width-percent-100">
              <div>Select Team</div>
            </div>
          }
          rules={[
            {
              required: agencyList?.length < 1,
              message: 'This field is required!',
            },
          ]}
        >
          <CommonDropdown
            id="add-user-agencies-dropdown"
            allowClear
            disabled={false}
            showSearch
            mode="multiple"
            placeholder="Select Team"
            optionFilterProp="children"
            query={GLOBAL_AGENCY_DROPDOWN_LIST}
            fetchPolicy="network-only"
            responsePath="globalAgencyDropdownList.data"
            valuePath="id"
            labelPath="name"
            optionKey="contact-users"
            showExtraLoader
            disableCondition={(item) =>
              item?.type === 'DEFAULT' &&
              isUpdate &&
              includes(userData?.roles, USER_ROLES.SYSTEM_ADMIN)
            }
            onChange={(id) => {
              let finalSelectedAgencies = [];
              if (
                id?.length === 0 &&
                includes(userData?.roles, USER_ROLES.SYSTEM_ADMIN)
              ) {
                const defaultTeams = filter(
                  globalAgencyList,
                  (value) => value?.type === 'DEFAULT',
                );
                finalSelectedAgencies = defaultTeams;
                setShowMainTeamRemoveNote(true);

                form?.setFieldsValue({
                  agencyIds: uniqBy(defaultTeams, 'id')?.map(
                    (item) => item?.id,
                  ),
                });
              } else {
                finalSelectedAgencies = filter(globalAgencyList, (value) =>
                  includes(id, value?.id),
                );
                setShowMainTeamRemoveNote(false);
              }
              setSelectedAgencyData(finalSelectedAgencies);

              const initialIds =
                initialAgencyData?.map((agency) => agency?.id) || [];
              const currentIds =
                finalSelectedAgencies?.map((agency) => agency?.id) || [];

              const hasRemovedAgencies = initialIds?.some(
                (initialId) => !currentIds?.includes(initialId),
              );
              const userHasProjects = !!userData?.projectUsers?.length;

              setShowWarningMessage(hasRemovedAgencies && userHasProjects);
            }}
            variables={{
              filter: {
                userId: Number(userData?.id),
              },
            }}
            addExtraData={selectedAgencyData}
            customOptions={selectedAgencyData || []}
            callback={(e) =>
              setGlobalAgencyList((prev) =>
                flattenDeep([...prev, e?.globalAgencyDropdownList?.data]),
              )
            }
          />
        </Form.Item>
        {showMainTeamRemoveNote && (
          <div className="text-warning note-margin">
            System Admin cannot be removed from Main Team
          </div>
        )}
        {showWarningMessage && (
          <div className="mb-10">
            <strong>Note:</strong> Removing the team will also remove the user
            from the project if they are added through this team.
          </div>
        )}
        <Divider dashed />
      </>
      <div className="create-agency-title">Create Team</div>
      {agencyList?.length > 0 &&
        map(agencyList, (user, index) => (
          <>
            <Row
              gutter={10}
              align="middle"
              justify="space-between"
              className="mt-15"
              key={index}
            >
              <Col span={22}>
                <Card className="user-card">
                  <Row justify="space-between" align="middle">
                    <Col span={20}>
                      <EllipsisText text={user?.name} />
                    </Col>
                    <Col span={2}>
                      <CommonTooltip
                        trigger="focus"
                        title="Edit"
                        placement="bottom"
                      >
                        <Button
                          id={`edit-agency-button-${index}`}
                          shape="round"
                          disabled={false}
                          className="b-0"
                          icon={<EditButton height={28} width={28} />}
                          onClick={() => handleEdit(user)}
                        />
                      </CommonTooltip>
                    </Col>
                  </Row>
                </Card>
              </Col>
              <Col span={2}>
                <Popconfirm
                  title="Are you sure you want to remove this team?"
                  onConfirm={() => {
                    agencyList?.splice(index, index + 1);
                    setAgencyList([...agencyList]);
                  }}
                  okText="Yes"
                  cancelText="No"
                  okButtonProps={{
                    disabled: false,
                  }}
                  cancelButtonProps={{
                    disabled: false,
                  }}
                  disabled={false}
                >
                  <CommonTooltip
                    trigger="focus"
                    title="Remove"
                    placement="bottom"
                  >
                    <Button
                      id={`remove-agency-button-${index}`}
                      disabled={false}
                      className="b-0"
                      icon={<MinusCircle />}
                    />
                  </CommonTooltip>
                </Popconfirm>
              </Col>
            </Row>
            {filter(
              map(agencyList, 'name'),
              (data) => data?.toLowerCase() === user?.name?.toLowerCase(),
            )?.length > 1 && (
              <Row>
                <div className="text-danger mt-5">Team name already exists</div>
              </Row>
            )}
          </>
        ))}
      <Button
        id="add-user-add-team-button"
        type="link"
        disabled={false}
        className="mt-15"
        icon={<PlusOutlined />}
        onClick={() => {
          setSetShowAgencyModal(true);
        }}
      >
        Add Team
      </Button>
    </>
  );
  const renderFrom = {
    0: step1Form,
    1: step2Form,
  };
  return (
    <div id="add-edit-user">
      {showAgencyModal && (
        <AddAgencyModal
          showModal={showAgencyModal}
          setShowModal={setSetShowAgencyModal}
          agencyData={agencyData}
          isUpdate={!!agencyData}
          setAgencyData={setAgencyData}
          setAgencyList={setAgencyList}
          agencyList={agencyList}
          onlyGetAgencyData
          agencyId={findIndex(agencyList, agencyData)}
        />
      )}
      <Modal
        maskClosable={false}
        centered
        open={showModal}
        confirmLoading={createUserLoading || updateUserLoading}
        onOk={handleAdd}
        className="dialog"
        okText={isUpdate ? 'Save' : 'Add'}
        onCancel={handleCancel}
        closeIcon={<WrongIcon />}
        footer={null}
        getContainer={() =>
          // eslint-disable-next-line no-undef
          document.getElementById('add-edit-user')
        }
      >
        <div
          className={clsx(
            'd-flex align-center',
            isTabletViewport && 'justify-center',
          )}
        >
          <Steps
            className="mb-24 mt-5"
            current={current}
            items={[
              ...map(keys(ADD_USER_STEPS), (stepItem) => {
                return {
                  key: stepItem,
                  title: ADD_USER_STEPS[stepItem],
                  id: `step-${stepItem}`,
                };
              }),
            ]}
          />
        </div>
        <Form
          form={form}
          initialValues={
            userData
              ? {
                  ...userData,
                  phoneNo: userData?.phoneNo?.replace(
                    userData?.countryCode,
                    '',
                  ),
                  agencyIds: selectedAgencyData,
                }
              : { isActive: true, countryCode: '+91' }
          }
          layout="vertical"
          onValuesChange={() => {
            if (alreadyAddedUser) {
              setAlreadyAddedUser();
            }
          }}
          onFinish={onUserSubmitFinish}
        >
          {renderFrom[current]}
          {alreadyAddedUser && current === 1 && (
            <div className="existing-user-info">
              <h3>User Details Already Exist</h3>
              <p>The details you entered match below user’s existing record.</p>
              <CommonCard
                variant="outlined"
                className="existing-user-info-card"
              >
                <div>
                  <div>
                    <b>Name : </b> {alreadyAddedUser?.name || '-'}
                  </div>
                  <div>
                    <b>Email : </b> {alreadyAddedUser?.email || '-'}
                  </div>
                  <div>
                    <b>Mobile Number : </b>
                    {alreadyAddedUser?.phoneNo || '-'}
                  </div>
                  <p>Either keep same or update the existing record.</p>
                </div>
              </CommonCard>
            </div>
          )}
          {current === 1 && (
            <div
              className={clsx(
                'd-flex align-center',
                alreadyAddedUser ? 'justify-center' : 'justify-end',
              )}
            >
              {alreadyAddedUser ? (
                <Row
                  justify={
                    !alreadyAddedUser?.isActive &&
                    !alreadyAddedUser?.isUserExistWithSameEmailAndPhone
                      ? 'space-between'
                      : 'center'
                  }
                  align="middle"
                  className="width-percent-100 form-buttons-Row"
                  gutter={10}
                >
                  {!alreadyAddedUser?.isActive &&
                    !alreadyAddedUser?.isUserExistWithSameEmailAndPhone && (
                      <Col span={isDesktopViewport ? 12 : 24}>
                        <Button
                          id="add-user-update-existing-button"
                          disabled={false}
                          shape="round"
                          onClick={() => {
                            const formValues = form.getFieldsValue();
                            onUserSubmitFinish(formValues, true);
                          }}
                          type="link"
                          className="width-percent-100"
                        >
                          Update With Entered Details
                        </Button>
                      </Col>
                    )}
                  <Col span={isDesktopViewport ? 12 : 24}>
                    <Button
                      id="add-user-add-existing-button"
                      disabled={false}
                      type="primary"
                      shape="round"
                      className={clsx(
                        'width-percent-100',
                        !isDesktopViewport && 'mt-10',
                      )}
                      onClick={() => {
                        form.submit();
                      }}
                    >
                      Add With Existing Details
                    </Button>
                  </Col>
                </Row>
              ) : (
                <>
                  <Button
                    id="add-user-previous-cancel-button"
                    shape="round"
                    type="text"
                    className="cancel-button"
                    disabled={false}
                    onClick={() => {
                      if (current === 1) {
                        setGlobalAgencyList([]);
                        setCurrent(0);
                      } else {
                        handleCancel();
                      }
                    }}
                  >
                    {current === 1 ? `< Previous` : 'Cancel'}
                  </Button>
                  <Popconfirm
                    title="You are about to update user information. Are you sure?"
                    okText="Yes"
                    cancelText="No"
                    disabled={!isUpdate}
                    okButtonProps={{ shape: 'round' }}
                    cancelButtonProps={{ shape: 'round' }}
                    placement="topRight"
                    onConfirm={() => {
                      form.submit();
                    }}
                  >
                    <Button
                      id="add-user-save-add-button"
                      shape="round"
                      className="save-button"
                      type="primary"
                      onClick={() => {
                        if (!isUpdate) {
                          form.submit();
                        }
                      }}
                    >
                      {isUpdate ? 'Save User' : 'Add User'}
                    </Button>
                  </Popconfirm>
                </>
              )}
            </div>
          )}
          {current === 1 && alreadyAddedUser && (
            <div className="d-flex justify-center align-center mt-10">
              <Button
                id="add-user-final-previous-button"
                shape="round"
                type="text"
                className="cancel-button"
                disabled={false}
                onClick={() => {
                  if (current === 1) {
                    setCurrent(0);
                  } else {
                    handleCancel();
                  }
                }}
              >
                {current === 1 ? `< Previous` : 'Cancel'}
              </Button>
            </div>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default AddUserModal;
